import React, { useState, useEffect } from "react";
import { Select, Button, Table, Input } from "antd";
import { EditOutlined, CheckOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import '../assets/styles/common.css';

const { Option } = Select;

const sampleData = [
    {
        id: "comp1",
        name: "Component 1",
        componentSteps: [
            {
                messageCode: "WELCOME-SINGLE-PATIENT",
                messages: [
                    "c9a1f7b3-5d62-4e1a-9b07-3e8f2d6c1a45",
                    "f3b8d7a2-9c41-482e-85d6-7a0c4e1b293f",
                ],
                fallback1: "7e9a3c2b-1f65-48d4-92a8-0b6e5d7f3c12",
                fallback2: "a4d6e2b7-9f30-4c8a-95d3-6b7e1f2c0845",
                functionCall: { functionName: "", prompt: "" },
            }, {
                messageCode: "WELCOME-NO-PATIENT",
                messages: [
                    "c9a1f7b3-5d62-4e1a-9b07-3e8f2d6c1a45",
                    "f3b8d7a2-9c41-482e-85d6-7a0c4e1b293f",
                ],
                fallback1: "7e9a3c2b-1f65-48d4-92a8-0b6e5d7f3c12",
                fallback2: "a4d6e2b7-9f30-4c8a-95d3-6b7e1f2c0845",
                functionCall: { functionName: "", prompt: "" },
            },
        ],
    },
];

const messageMap = {
    "c9a1f7b3-5d62-4e1a-9b07-3e8f2d6c1a45": "Hello! Welcome to our service.",
    "f3b8d7a2-9c41-482e-85d6-7a0c4e1b293f": "How can I assist you today?",
    "7e9a3c2b-1f65-48d4-92a8-0b6e5d7f3c12": "I'm sorry, I didn't understand that.",
    "a4d6e2b7-9f30-4c8a-95d3-6b7e1f2c0845": "Could you please rephrase your request?",
};

// Create a standalone Editable component that manages its own state
const EditableCell = ({ messageKey, initialValue, onSave }) => {
    const [editing, setEditing] = useState(false);
    const [inputValue, setInputValue] = useState(initialValue || "");

    const handleEdit = (e) => {
        e.stopPropagation();
        setEditing(true);
        setInputValue(initialValue || "");
    };

    const handleChange = (e) => {
        console.log("Input value changing to:", e.target.value);
        setInputValue(e.target.value);
    };

    const handleSave = (e) => {
        e.stopPropagation();
        onSave(messageKey, inputValue);
        setEditing(false);
    };

    console.log("EditableCell rendering:", messageKey, "editing:", editing, "value:", inputValue);

    return (
        <div onClick={(e) => e.stopPropagation()}>
            {editing ? (
                <div style={{ display: "flex", gap: "8px" }} onClick={(e) => e.stopPropagation()}>
                    <Input
                        value={inputValue}
                        onChange={handleChange}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        autoFocus
                    />
                    <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={handleSave}
                    >
                        Update
                    </Button>
                </div>
            ) : (
                <span>
                    {initialValue} {" "}
                    <EditOutlined
                        onClick={handleEdit}
                        style={{ cursor: "pointer", marginLeft: 8 }}
                    />
                </span>
            )}
        </div>
    );
};

const WorkflowComponents = ({ wfId }) => {
    const [selectedComponents, setSelectedComponents] = useState([]);
    const [data, setData] = useState([]);
    const [formattedComponents, setFormattedComponents] = useState([]);
    const [updateCounter, setUpdateCounter] = useState(0); // Used to force rerenders

    // Fetch messages when selected components change
    const fetchMessages = () => {
        const filteredData = sampleData.filter((comp) => selectedComponents.includes(comp.id));
        setFormattedComponents(filteredData);
    };

    // Format data whenever dependencies change
    useEffect(() => {
        if (formattedComponents.length > 0) {
            setData(formatData(formattedComponents));
        }
    }, [formattedComponents, updateCounter]); // Re-format when update counter changes

    const handleSave = (messageKey, newValue) => {
        console.log("Saving edit for key:", messageKey, "with value:", newValue);

        if (newValue.trim()) {
            // Find the message in our data structure
            formattedComponents.forEach(component => {
                component.componentSteps.forEach(step => {
                    // Check messages array
                    const msgIndex = step.messages.indexOf(messageKey);
                    if (msgIndex !== -1) {
                        const newKey = uuidv4();
                        messageMap[newKey] = newValue;
                        step.messages[msgIndex] = newKey;
                        delete messageMap[messageKey];
                    }

                    // Check fallbacks
                    if (step.fallback1 === messageKey) {
                        const newKey = uuidv4();
                        messageMap[newKey] = newValue;
                        step.fallback1 = newKey;
                        delete messageMap[messageKey];
                    }

                    if (step.fallback2 === messageKey) {
                        const newKey = uuidv4();
                        messageMap[newKey] = newValue;
                        step.fallback2 = newKey;
                        delete messageMap[messageKey];
                    }
                });
            });

            // Force a re-render by updating a counter
            setUpdateCounter(updateCounter + 1);
        }
    };

    const formatData = (components) => {
        return components.map((component) => ({
            key: component.id,
            title: component.name,
            children: component.componentSteps.map((step, stepIndex) => ({
                key: `${component.id}-step-${stepIndex}`,
                title: step.messageCode,
                children: [
                    {
                        key: `${component.id}-messages-${stepIndex}`,
                        title: "Messages",
                        children: step.messages.map((msg, index) => ({
                            key: `msg-${msg}-${index}-${updateCounter}`, // Include updateCounter for forced refreshes
                            title: (
                                <EditableCell
                                    key={`${msg}-${updateCounter}`}
                                    messageKey={msg}
                                    initialValue={messageMap[msg] || msg}
                                    onSave={handleSave}
                                />
                            ),
                        })),
                    },
                    {
                        key: `${component.id}-fallback-${stepIndex}`,
                        title: "Fallbacks",
                        children: [
                            {
                                key: `fallback1-${step.fallback1}-${updateCounter}`,
                                title: (
                                    <EditableCell
                                        key={`${step.fallback1}-${updateCounter}`}
                                        messageKey={step.fallback1}
                                        initialValue={messageMap[step.fallback1] || step.fallback1}
                                        onSave={handleSave}
                                    />
                                )
                            },
                            {
                                key: `fallback2-${step.fallback2}-${updateCounter}`,
                                title: (
                                    <EditableCell
                                        key={`${step.fallback2}-${updateCounter}`}
                                        messageKey={step.fallback2}
                                        initialValue={messageMap[step.fallback2] || step.fallback2}
                                        onSave={handleSave}
                                    />
                                )
                            },
                        ],
                    },
                ],
            })),
        }));
    };

    const columns = [
        {
            title: "Component Messages",
            dataIndex: "title",
            key: "title",
        },
    ];

    return (
        <div style={{ padding: 20, maxWidth: 800, margin: "0 auto" }}>
            <div style={{ marginBottom: 20 }}>
                <Select
                    mode="multiple"
                    style={{ width: "60%" }}
                    placeholder="Select Components"
                    onChange={setSelectedComponents}
                    value={selectedComponents}
                >
                    <Option value="comp1">Component 1</Option>
                </Select>
                <Button type="primary" onClick={fetchMessages} style={{ marginLeft: 10 }}>
                    Load Messages
                </Button>
            </div>
            <Table
                columns={columns}
                dataSource={data}
                pagination={false}
                expandable={{ childrenColumnName: "children" }}
                bordered
                size="middle"
                style={{ background: "#fff", padding: "10px", borderRadius: "8px" }}
                components={{
                    header: {
                        cell: ({ children }) => (
                            <th style={{ background: "#f0f2f5", fontWeight: "bold", padding: "15px" }}>
                                {children}
                            </th>
                        ),
                    },
                }}
            />

        </div>
    );
};

export default WorkflowComponents;